<template>
  <div>
    <load-profile />
    <n-search-container
      :fields="searchFields"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      />
    </n-search-container>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            >
              <template #no-options>
                {{ $t("selection.noOption") }}
              </template>
            </v-select>
          </b-col>
          <b-col
            v-if="$can('create', permissionType)"
            class="text-right"
          >
            <b-button
              variant="primary"
              @click="showModal"
            >
              {{ $t('button.create') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :type="permissionType"
          :route="route"
          :updatable="false"
          @sort-changed="sortChanged"
          @destroy="destroy"
        >
          <template #cell(employee)="row">
            {{ row.item.merchantUserId.firstName }} {{ row.item.merchantUserId.lastName }}
          </template>
          <template #cell(date)="row">
            {{ row.item.startTime | formatDate('LL HH:mm') }} - {{ row.item.endTime | formatDate('LL HH:mm') }}
          </template>
          <template #front-button="row">
            <b-button
              v-b-tooltip.hover
              :title="$t('general.update')"
              variant="primary"
              pill
              size="sm"
              @click="editItem(row.item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>

    <!-- Form Modal -->
    <b-modal
      v-model="isModalVisible"
      :title="modalTitle"
      hide-footer
      @hidden="resetForm"
    >
      <validation-observer
        ref="formValidation"
        #default="{ invalid }"
      >
        <validation-provider
          v-slot="{ errors }"
        >
          <b-form-group
            :label="$t('breadcrumb.employee')"
            label-for="employee"
          >
            <n-async-single-select
              v-model="form.merchantUserId"
              name="operator"
              repository="allOperator"
              selection-key="id"
              selection-label="name"
              :is-custom-params="true"
              :clearable="false"
              :state="errors.length > 0 ? false : null"
              :always-fresh="true"
              :must-excluded-ids="[]"
              :placeholder="$t('placeholder.selectEmployee')"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.date')"
        >
          <b-form-group
            :label="$t('field.date')"
            label-for="date"
          >
            <b-form-datepicker
              id="date"
              v-model="form.date"
              :state="errors.length > 0 ? false : null"
              v-bind="labels[$i18n.locale] || {}"
              :locale="$i18n.locale"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.startTime')"
          mode="passive"
        >
          <b-form-group
            :label="$t('field.startTime')"
            label-for="startTime"
          >
            <n-time-picker
              v-model="form.startTime"
              :locale="$i18n.locale"
              :placeholder="$t('placeholder.selectStartTime')"
              :state="errors.length > 0 ? false : null"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.endTime')"
          mode="passive"
        >
          <b-form-group
            :label="$t('field.endTime')"
            label-for="endTime"
          >
            <n-time-picker
              v-model="form.endTime"
              :locale="$i18n.locale"
              :placeholder="$t('placeholder.selectEndTime')"
              :state="errors.length > 0 ? false : null"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <b-form-invalid-feedback
          v-if="timeRangeError"
          :state="!timeRangeError"
          class="d-block mt-1"
        >
          {{ $t('validations.endTimeMustBeAfterStart') }}
        </b-form-invalid-feedback>

        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.remark')"
        >
          <b-form-group
            :label="$t('field.remark')"
            label-for="remark"
          >
            <b-form-textarea
              id="remark"
              v-model="form.remark"
              :placeholder="$t('placeholder.enterRemark')"
              rows="3"
              :state="errors.length > 0 ? false : null"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>

        </validation-provider>

        <b-row class="mt-3">
          <b-col class="text-right">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="isModalVisible = false"
            >
              {{ $t('button.cancel') }}
            </b-button>
            <b-button
              variant="primary"
              :disabled="
                invalid || submitting || !isFormValid || timeRangeError
              "
              @click="onSubmit"
            >
              <span
                v-if="submitting"
                class="spinner-border spinner-border-sm mr-1"
              />
              {{ isEditing ? $t('button.update') : $t('button.save') }}
            </b-button>
          </b-col>
        </b-row>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BModal,
  BForm,
  BFormGroup,
  BFormDatepicker,
  BFormTextarea,
  BFormInvalidFeedback,
} from 'bootstrap-vue'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect.vue'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination'
import NTable from '@/components/NTable'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import NTimePicker from '@/components/NTimePicker.vue'
import NSearchContainer from '@/components/NSearchContainer'
import NSearchInput from '@/components/NSearchInput'
import VSelect from 'vue-select'
import SearchInputs from './searchInput'
import tableField from './tableField'
import labels from '@/data/localeDatepicker';

const TakeLeaveRepository = Repository.get('takeLeave')

export default {
  name: 'TakeLeaveList',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormDatepicker,
    BFormTextarea,
    NAsyncSingleSelect,
    NPagination,
    NTable,
    BFormInvalidFeedback,
    ValidationProvider,
    ValidationObserver,
    NTimePicker,
    NSearchContainer,
    NSearchInput,
    VSelect,
  },
  data() {
    return {
      items: [],
      loading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        merchantUserId: this.$route.query.merchantUserId || null,
        date: this.$route.query.date || null,
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        merchantUserId: this.$route.query.merchantUserId || null,
        date: this.$route.query.date || null,
      },
      isModalVisible: false,
      isEditing: false,
      form: {
        merchantUserId: null,
        date: '',
        startTime: '',
        endTime: '',
        remark: '',
      },
      selectedItem: null,
      submitting: false,
      timeRangeError: false,
      labels
    }
  },
  computed: {
    modalTitle() {
      return this.isEditing
        ? this.$t('breadcrumb.updateTakeLeave')
        : this.$t('breadcrumb.createTakeLeave')
    },
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
    isFormValid() {
      return Boolean(
        this.form.merchantUserId
            && this.form.date
            && this.form.startTime
            && this.form.endTime
            && this.form.remark,
      )
    },
    activeSearch() {
      return !!this.$route.query.employee || !!this.$route.query.status || !!this.$route.query.dateRange
    },
  },
  watch: {
    perPage() {
      this.list(1)
    },
  },
  mounted() {
    this.getData()
  },
  methods: {
    updateQueryString() {
      this.$router.replace({ query: this.query })
    },
    list(page = 1) {
      this.query.page = page
      this.updateQueryString()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.query.order = sortBy
      this.query.sort = sortDirection
      this.updateQueryString()
      this.getData()
    },
    getData() {
      this.loading = true
      TakeLeaveRepository.list(this.query)
        .then(response => {
          const { list } = response.data.data
          this.items = [...list]
          this.total = response.data.data.total
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    showModal() {
      this.resetForm()
      this.isModalVisible = true
      this.isEditing = false
    },
    editItem(item) {
      this.selectedItem = item
      try {
        const startDateTime = new Date(item.startTime)
        const endDateTime = new Date(item.endTime)

        // Validate dates before using them
        if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
          throw new Error('Invalid date')
        }

        this.form = {
          merchantUserId: item.merchantUserId.id,
          date: startDateTime.toISOString().split('T')[0],
          startTime: startDateTime.toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
          endTime: endDateTime.toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
          remark: item.remark,
        }
        this.isEditing = true
        this.isModalVisible = true
      } catch (error) {
        console.error('Error parsing dates:', error)
        this.$toast.error(this.$t('errors.invalidDateTime'))
      }
    },
    destroy(id) {
      TakeLeaveRepository.destroy(id)
        .then(() => {
          this.getData()
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    validateTimeRange() {
      if (this.form.startTime && this.form.endTime) {
        const parseTime = timeStr => {
          const [hours, minutes] = timeStr.split(':').map(Number)
          return hours * 60 + minutes
        }

        const startMinutes = parseTime(this.form.startTime)
        const endMinutes = parseTime(this.form.endTime)

        this.timeRangeError = endMinutes <= startMinutes
      } else {
        this.timeRangeError = false
      }
    },
    async onSubmit() {
      const isValid = await this.$refs.formValidation.validate()

      if (!isValid || this.submitting || this.timeRangeError) return

      this.submitting = true

      try {
        const [year, month, day] = this.form.date.split('-').map(Number)
        const [startHours, startMinutes] = this.form.startTime.split(':').map(Number)
        const [endHours, endMinutes] = this.form.endTime.split(':').map(Number)

        // JavaScript months are 0-based, so subtract 1 from month
        const startDate = new Date(Date.UTC(year, month - 1, day, startHours - 7, startMinutes))
        const endDate = new Date(Date.UTC(year, month - 1, day, endHours - 7, endMinutes))

        const payload = {
          ...this.form,
          startTime: startDate.toISOString(),
          endTime: endDate.toISOString(),
          departmentId: this.$route.params.departmentId,
        }
        delete payload.date

        await (this.isEditing
          ? TakeLeaveRepository.update(this.selectedItem.id, payload)
          : TakeLeaveRepository.create(payload))

        this.getData()
        this.isModalVisible = false
      } catch (error) {
        console.error('Error processing dates:', error)
        this.$toast.error(this.$t('errors.invalidDateTime'))
      } finally {
        this.submitting = false
      }
    },
    resetForm() {
      const now = new Date()
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`

      this.form = {
        merchantUserId: null,
        date: now.toISOString().split('T')[0], // Current date in YYYY-MM-DD format
        startTime: currentTime,
        endTime: '',
        remark: '',
      }
      this.selectedItem = null
      this.timeRangeError = false
      this.$nextTick(() => {
        this.$refs.formValidation?.reset()
      })
    },
    search() {
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQueryString()
      this.getData()
    },
    reset() {
      this.$refs.search.reset()
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQueryString()
      this.getData()
    },
  },
  setup() {
    const permissionType = 'manage-users'
    const route = 'take-leave'
    const fields = tableField
    const searchFields = SearchInputs
    return {
      permissionType, route, fields, searchFields,
    }
  },
}
</script>
